export type EventListener<TPayload> = (payload: TPayload) => void;

export type Unsubscribe = () => void;

export interface EventBridge<TEvents extends object> {
  emit<TKey extends keyof TEvents>(
    event: TKey,
    payload: TEvents[TKey]
  ): void;
  subscribe<TKey extends keyof TEvents>(
    event: TKey,
    listener: EventListener<TEvents[TKey]>
  ): Unsubscribe;
}

type ListenerRegistry<TEvents extends object> = {
  [TKey in keyof TEvents]?: Set<EventListener<TEvents[TKey]>>;
};

export function createEventBridge<
  TEvents extends object
>(): EventBridge<TEvents> {
  const listeners: ListenerRegistry<TEvents> = {};

  return {
    emit(event, payload) {
      const eventListeners = listeners[event];

      if (!eventListeners) {
        return;
      }

      for (const listener of [...eventListeners]) {
        listener(payload);
      }
    },

    subscribe(event, listener) {
      const eventListeners =
        listeners[event] ?? new Set<EventListener<TEvents[typeof event]>>();

      listeners[event] = eventListeners;
      eventListeners.add(listener);

      let isSubscribed = true;

      return () => {
        if (!isSubscribed) {
          return;
        }

        isSubscribed = false;
        eventListeners.delete(listener);

        if (eventListeners.size === 0) {
          delete listeners[event];
        }
      };
    }
  };
}
